import React from 'react';
import useSEO from '../hooks/useSEO';

export default function Handelsbetingelser() {
  useSEO(
    'Handelsbetingelser & Bestillingsinfo | Chokolenten ApS',
    'Læs vores handelsbetingelser, betalingsbetingelser og vigtig information om bestilling af vores håndlavede produkter.'
  );

  return (
    <div className="pb-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-choc-dark text-cream-light overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-t from-choc-dark via-choc-dark/90 to-transparent" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center gap-4">
          <span className="text-xs font-sans uppercase tracking-[0.25em] text-berry-light font-bold">
            Handel & Levering
          </span>
          <h1 className="text-4xl sm:text-5xl font-serif font-bold text-cream-light tracking-wide">
            Handelsbetingelser & Bestillingsinfo
          </h1>
          <p className="max-w-2xl text-sm sm:text-base text-cream-light/80 leading-relaxed font-sans">
            Vigtig information om bestilling, betaling og vores håndlavede produkter.
          </p>
        </div>
      </section>

      {/* Indhold */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          
          {/* Venstre side: Hurtig oversigt / Info-bokse */}
          <div className="lg:col-span-1 flex flex-col gap-8">
            
            {/* Bestillingsfrist boks */}
            <div className="bg-berry-accent text-white p-6 rounded-2xl shadow-sm flex flex-col gap-4">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white">
                <i className="fa-solid fa-clock text-lg" />
              </div>
              <div>
                <h3 className="font-serif font-bold text-lg mb-1">Bestillingsfrist</h3>
                <p className="text-sm text-white/90 font-sans leading-relaxed">
                  Bestilles senest <strong className="text-white">mandag kl. 10:00</strong>. Alle produkter produceres frisk efter bestilling.
                </p>
              </div>
            </div>

            {/* Levering boks */}
            <div className="bg-choc-dark text-cream-light p-6 rounded-2xl shadow-sm flex flex-col gap-4">
              <div className="w-10 h-10 rounded-full bg-cream-light/10 flex items-center justify-center text-cream-light">
                <i className="fa-solid fa-truck text-lg" />
              </div>
              <div>
                <h3 className="font-serif font-bold text-lg mb-1">Levering</h3>
                <p className="text-sm text-cream-light/90 font-sans leading-relaxed">
                  Levering sker typisk <strong className="text-berry-light">torsdag</strong> med Danske Fragtmænd.
                </p>
              </div>
            </div>

            {/* Betaling boks */}
            <div className="bg-cream-light text-choc-dark p-6 rounded-2xl border border-choc-dark/10 shadow-sm flex flex-col gap-4">
              <div className="w-10 h-10 rounded-full bg-berry-accent/10 flex items-center justify-center text-berry-accent">
                <i className="fa-solid fa-credit-card text-lg" />
              </div>
              <div>
                <h3 className="font-serif font-bold text-lg text-choc-dark mb-1">Betaling</h3>
                <p className="text-sm text-choc-dark/80 font-sans leading-relaxed">
                  <strong className="text-choc-dark">Netto 8 dage.</strong> Bemærk, at nye leverancer først sendes, når tidligere faktura er betalt.
                </p>
              </div>
            </div>

            {/* Virksomhedsinfo boks */}
            <div className="bg-cream-light text-choc-dark p-6 rounded-2xl border border-choc-dark/10 shadow-sm flex flex-col gap-4">
              <div className="w-10 h-10 rounded-full bg-berry-accent/10 flex items-center justify-center text-berry-accent">
                <i className="fa-solid fa-building text-lg" />
              </div>
              <div>
                <h3 className="font-serif font-bold text-lg text-choc-dark mb-1">Virksomhedsoplysninger</h3>
                <p className="text-sm text-choc-dark/80 font-sans leading-relaxed">
                  <strong>Chokolenten ApS</strong><br />
                  CVR: 44157438<br />
                  Bytorvet 30, 7730 Hanstholm
                </p>
              </div>
            </div>

          </div>

          {/* Højre side: Detaljeret tekst */}
          <div className="lg:col-span-2 bg-cream-light rounded-2xl border border-choc-dark/10 shadow-sm p-8 sm:p-10 font-sans text-choc-dark/80 leading-relaxed flex flex-col gap-10">
            
            {/* Bestilling & Levering */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-berry-accent/10 flex items-center justify-center text-berry-accent">
                  <i className="fa-solid fa-calendar-check" />
                </div>
                <h2 className="text-2xl font-serif font-bold text-choc-dark">Bestilling & Levering</h2>
              </div>
              <p className="mb-4">
                Da alle vores produkter er håndlavede og bages/fremstilles frisk, arbejder vi med faste ugentlige produktionscyklusser:
              </p>
              <ul className="list-disc pl-6 flex flex-col gap-2">
                <li><strong className="text-choc-dark">Bestillingsfrist:</strong> Bestillinger skal være os i hænde senest <strong className="text-choc-dark">mandag kl. 10:00</strong> for at komme med i ugens produktion.</li>
                <li><strong className="text-choc-dark">Frisk produktion:</strong> Vi producerer udelukkende efter bestilling for at sikre maksimal friskhed og holdbarhed.</li>
                <li><strong className="text-choc-dark">Levering:</strong> Varerne sendes afsted, så de typisk leveres <strong className="text-choc-dark">torsdag</strong> med Danske Fragtmænd.</li>
              </ul>
            </div>

            {/* Betalingsbetingelser */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-berry-accent/10 flex items-center justify-center text-berry-accent">
                  <i className="fa-solid fa-file-invoice-dollar" />
                </div>
                <h2 className="text-2xl font-serif font-bold text-choc-dark">Betalingsbetingelser</h2>
              </div>
              <p className="mb-2">
                For erhvervskunder og faste aftaler gælder følgende betalingsbetingelser:
              </p>
              <ul className="list-disc pl-6 flex flex-col gap-2">
                <li><strong className="text-choc-dark">Betalingsfrist:</strong> Netto 8 dage fra fakturadato.</li>
                <li><strong className="text-choc-dark">Sikkerhed for betaling:</strong> For at sikre en sund samhandel sendes nye leverancer først, når alle tidligere forfaldne fakturaer er betalt.</li>
              </ul>
            </div>

            {/* Vigtig produktinformation */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-berry-accent/10 flex items-center justify-center text-berry-accent">
                  <i className="fa-solid fa-cookie" />
                </div>
                <h2 className="text-2xl font-serif font-bold text-choc-dark">Vigtig produktinformation (Thy Kukies)</h2>
              </div>
              <p className="mb-4">
                Vores populære Thy Kukies er håndlavede Kukies med fyld, som bages hver mandag efter bestilling.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="bg-white p-5 rounded-xl border border-choc-dark/5">
                  <h4 className="font-serif font-bold text-choc-dark mb-2">Specifikationer</h4>
                  <ul className="list-disc pl-5 text-sm flex flex-col gap-1.5">
                    <li>Håndlavede Kukies med fyld.</li>
                    <li>Bages hver mandag efter bestilling.</li>
                    <li><strong className="text-choc-dark">Holdbarhed:</strong> 4-5 uger.</li>
                    <li>Leveres i æsker med 10 stk.</li>
                  </ul>
                </div>
                <div className="bg-white p-5 rounded-xl border border-choc-dark/5">
                  <h4 className="font-serif font-bold text-choc-dark mb-2">Opbevaring</h4>
                  <ul className="list-disc pl-5 text-sm flex flex-col gap-1.5">
                    <li>Opbevares ved stuetemperatur eller i chokoladekøler.</li>
                    <li>Kan pakkes i cellofanposer.</li>
                    <li>Opbevares tørt og ikke for varmt.</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Godt at vide */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-choc-dark">
              <div className="flex items-center gap-2 mb-3">
                <i className="fa-solid fa-circle-info text-amber-600 text-lg" />
                <h3 className="font-serif font-bold text-lg">Godt at vide om vores håndlavede produkter</h3>
              </div>
              <ul className="list-disc pl-5 text-sm flex flex-col gap-2 text-choc-dark/90">
                <li>Da alle vores produkter er håndlavede, kan de variere naturligt i både størrelse og udseende. Det er en del af charmen ved ægte håndværk!</li>
                <li>Vores Kukies kan være både sprøde og bløde i konsistensen.</li>
                <li>En lys eller grålig farve på overfladen kan forekomme helt naturligt fra fyldet.</li>
                <li><strong className="text-choc-dark">Særligt vedr. transport:</strong> Chokoladeknap-varianten er mest udsat for transportskader og sendes derfor på eget ansvar.</li>
              </ul>
            </div>

            {/* Kontakt os */}
            <div className="border-t border-choc-dark/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-6">
              <div>
                <h4 className="font-serif font-bold text-lg text-choc-dark mb-1">Spørgsmål til handelsbetingelser?</h4>
                <p className="text-sm text-choc-dark/60">Vi sidder klar til at hjælpe dig på telefon eller e-mail.</p>
              </div>
              <a
                href="mailto:info@chokolenten.dk"
                className="bg-berry-accent hover:bg-berry-accent/90 text-white font-sans font-semibold px-6 py-3 rounded-xl text-sm transition-colors cursor-pointer shadow-sm whitespace-nowrap"
              >
                Skriv til os
              </a>
            </div>

          </div>

        </div>
      </section>
    </div>
  );
}
